import { adjustHslLightness, hexToHslChannels } from "@/utils/color"
import { normalizeRelayUrl } from "@/utils/url"
import type { AppConfig } from "./types"

const DEFAULT_DESCRIPTION = "A user-friendly Nostr client for exploring relay feeds."

function upsertLink(selector: string, create: () => HTMLLinkElement): HTMLLinkElement {
  const existing = document.head.querySelector<HTMLLinkElement>(selector)
  if (existing) return existing
  const element = create()
  document.head.appendChild(element)
  return element
}

function upsertMeta(name: string, content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!meta) {
    meta = document.createElement("meta")
    meta.name = name
    document.head.appendChild(meta)
  }
  meta.content = content
}

/**
 * Applies branding, design tokens and document metadata. Called once before the
 * first React render so the UI never flashes the bundled defaults.
 */
export function applyConfigToDocument(config: AppConfig): void {
  const root = document.documentElement
  const style = root.style

  const primary = hexToHslChannels(config.design.colors.primary)
  if (primary) {
    style.setProperty("--primary", primary)
    style.setProperty("--primary-hover", adjustHslLightness(primary, 7))
    style.setProperty("--primary-active", adjustHslLightness(primary, -6))
    style.setProperty("--primary-soft", primary.replace(/[\d.]+%$/, "94%"))
  }
  const accent = hexToHslChannels(config.design.colors.accent)
  if (accent) style.setProperty("--accent", accent)

  style.setProperty("--radius", `${config.design.radius}px`)
  style.setProperty("--radius-sm", `${Math.max(config.design.radius - 4, 2)}px`)
  style.setProperty("--radius-lg", `${config.design.radius + 6}px`)
  style.setProperty("--font-scale", String(config.design.fontScale))
  style.setProperty("--font-family", config.design.fontFamily)
  const density = config.design.density
  style.setProperty("--space-scale", density === "compact" ? "0.86" : density === "spacious" ? "1.16" : "1")

  document.title =
    config.branding.titleTemplate.replace("{name}", config.branding.name).replace("{page}", "").trim() ||
    config.branding.name

  const description = config.branding.description || DEFAULT_DESCRIPTION
  upsertMeta("description", description)
  upsertMeta("apple-mobile-web-app-title", config.branding.shortName)
  upsertMeta("theme-color", "#0a0a0a")

  if (config.branding.favicon) {
    upsertLink('link[rel="icon"]', () => {
      const link = document.createElement("link")
      link.rel = "icon"
      return link
    }).href = config.branding.favicon
  }
  if (config.branding.logo) {
    upsertLink('link[rel="apple-touch-icon"]', () => {
      const link = document.createElement("link")
      link.rel = "apple-touch-icon"
      return link
    }).href = config.branding.logo
  }

  root.lang = config.i18n.defaultLanguage
  root.style.setProperty("--density", density)
}

/** Sets the document title using the configured template. */
export function applyPageTitle(config: AppConfig, page?: string): void {
  const title = config.branding.titleTemplate
    .replace("{page}", page ?? "")
    .replace("{name}", config.branding.name)
    .replace(/^[\s·|—-]+|[\s·|—-]+$/g, "")
  document.title = title || config.branding.name
}

/** Normalises the configured relay lists once, dropping invalid entries. */
export function configuredRelays(config: AppConfig) {
  const clean = (urls: string[]) =>
    urls.map((url) => normalizeRelayUrl(url)).filter((url): url is string => Boolean(url))
  return {
    defaultRelays: clean(config.relays.defaultRelays),
    searchRelays: clean(config.relays.searchRelays),
    trendingRelays: clean(config.relays.trendingRelays),
    discoveryRelays: clean(config.relays.discoveryRelays),
  }
}
